import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './ChatPanel.css';
import {selectChatMessages, selectGameType, selectPlayers} from '../../redux/reducers/sharedStateReducer/selectors';
import {selectUserId} from '../../redux/reducers/privateStateReducer/selectors';
import {addChatMessage, showToast} from '../../redux/actions';
import {GAME_TYPES} from '../../constants/game';
import {getPlayerHexColor} from '../../utils/players/getPlayerHexColor';
import {addClassNameIfTrue} from '../../utils/other/addClassNameIfTrue';
import {getNetworkSocket} from '../../utils/networkSocket';
import {CHAT_MESSAGE_MAX_LENGTH, normalizeChatMessageText} from '../../utils/chat/chatMessage';
import {selectIsModeratedChat} from '../../redux/reducers/sharedStateReducer/matchChatSelectors';
import {
  getMatchChatSendBlock,
  MATCH_CHAT_SEND_BLOCK_KINDS,
  selectMatchChatState,
} from '../../redux/reducers/privateStateReducer/matchChatSelectors';
import {MATCH_CHAT_OUTGOING_STATUSES} from '../../redux/reducers/privateStateReducer/matchChatReducer';
import {
  createMatchChatClientMessageId,
  dismissMatchChatMessage,
  expireStaleMatchChatMessages,
  matchChatMessageBlocked,
  matchChatSendStarted,
} from '../../redux/reducers/privateStateReducer/matchChatActions';
import {MODERATED_CHAT_MAX_LENGTH, MODERATED_CHAT_MIN_INTERVAL_MS} from '../../configs/matchChat';
import {normalizeModeratedChatText, prefilterChatMessage} from '../../utils/chat/chatPrefilter';
import {getChatRejectionText} from '../../utils/chat/getChatRejectionText';
import AiPlayerMark from '../VisibleBotMark/AiPlayerMark';
import {GAME_BUTTON_SIZES, GameButton} from '../gameUi/GameButton';
import {GamePanel} from '../gameUi/GamePanel';
import {EngravedIcon} from '../gameUi/EngravedIcon';
import {useCollapseOnCornerPanelRequest} from '../../hooks/visuals/useCornerPanels';

// Safari подтверждает составной символ IME нажатием Enter, у которого
// isComposing уже false (compositionend приходит раньше keydown) — такое
// нажатие узнается только по keyCode 229
const IME_PROCESS_KEY_CODE = 229;

// Как часто модерируемый чат проверяет свои сообщения на «зависание» —
// ответ сервера мог потеряться при обрыве связи (см. matchChatReducer)
const STALE_OUTGOING_CHECK_INTERVAL_MS = 5000;

const MODERATED_CHAT_HINT = `Сообщения проверяет модератор · не чаще 1 раза в ${
  Math.round(MODERATED_CHAT_MIN_INTERVAL_MS / 1000)
} сек`;
const MODERATED_CHAT_PENDING_LIMIT_HINT = 'Дождитесь решения модератора по отправленным сообщениям';
const MODERATED_CHAT_PAUSED_TEXT = 'Чат временно недоступен';

const OUTGOING_STATUS_TEXTS = {
  [MATCH_CHAT_OUTGOING_STATUSES.SENDING]: 'отправляется…',
  [MATCH_CHAT_OUTGOING_STATUSES.PENDING]: 'на проверке у модератора…',
};

// Нажатия, набранные в поле сообщения, не должны доходить до обработчиков на
// window: Phaser перехватывает WASD и стрелки для камеры (и гасит их штатное
// действие — буквы ц/ф/ы/в и w/a/s/d не печатались бы), а оверлеи
// закрываются по Enter и пробелу (useCloseOnEnterSpace). React вешает
// обработчики на корень приложения, поэтому остановка всплытия здесь не
// пускает событие дальше к window.
// Отпускание клавиш (keyup) НЕ останавливаем: клавишу, нажатую до перехода
// фокуса в поле, Phaser иначе считал бы зажатой, и камера ехала бы сама
const stopKeyPropagation = (event) => {
  event.stopPropagation();
};

/**
 * Панель переписки игроков матча. Два режима:
 *
 * 1. Локальная разработка (немодерируемый). Через нее договариваются
 *    LLM-агенты, играющие по MCP (server/mcp), и живой игрок, который играет
 *    против них: участник сетевого матча пишет прямо здесь, зритель и
 *    просмотр записи только читают. Сообщения живут в общем состоянии матча
 *    (ADD_CHAT_MESSAGE), поэтому видны и в записи матча при воспроизведении.
 *    Гейт среды стоит в месте рендера (Footer): process.env.NODE_ENV
 *    заменяется сборкой на литерал, и в прод-сборке эта ветка удаляется как
 *    недостижимый код.
 *
 * 2. Модерируемый чат ИИ-матча (selectIsModeratedChat, и в прод-сборке).
 *    Свое сообщение клиент в общее состояние НЕ кладет: оно уходит серверу
 *    сырым CHAT_SEND, проходит очередь агента-модератора и приходит всем,
 *    включая отправителя, обычным ADD_CHAT_MESSAGE — только если одобрено.
 *    До тех пор отправитель видит его строкой «отправляется… / на проверке у
 *    модератора…», отклоненное — красной строкой с причиной (слайс matchChat).
 *    Лимиты те же, что у сервера (configs/matchChat.js); модератор недоступен —
 *    чат на паузе, игра идет. У сообщений ИИ — метка ИИ.
 *
 * Устройство повторяет ReactionsPanel: якорь фиксированного размера
 * кнопки-переключателя, разворот — position: absolute поверх соседей.
 */
const ChatPanel = ({onExpandedChange, isCollapseRequested = false}) => {
  const dispatch = useDispatch();
  const gameType = useSelector(selectGameType);
  const players = useSelector(selectPlayers);
  const messages = useSelector(selectChatMessages);
  const userId = useSelector(selectUserId);
  const isModerated = useSelector(selectIsModeratedChat);
  const matchChat = useSelector(selectMatchChatState);

  const [isExpanded, setIsExpanded] = useState(false);
  const [readCount, setReadCount] = useState(0);
  const [draft, setDraft] = useState('');
  // Перерисовка по окончании кулдауна: само время в состоянии не нужно,
  // нужен только повод перепроверить лимиты
  const [, setCooldownTick] = useState(0);
  const listRef = useRef(null);
  const formRef = useRef(null);
  const inputRef = useRef(null);

  // Писать может только участник идущего сетевого матча — от своего имени.
  // Зритель в составе игроков не числится, запись матча только проигрывается
  const me = gameType === GAME_TYPES.ONLINE
    ? (players || []).find((player) => player.id === userId)
    : null;
  const canWrite = Boolean(me);

  // Свои еще не доставленные сообщения — только у участника модерируемого
  // чата; пауза — только в идущем матче (запись матча просто читается)
  const outgoing = isModerated && canWrite ? matchChat.outgoing : [];
  const isChatPaused = isModerated && gameType === GAME_TYPES.ONLINE && matchChat.isPaused;
  const hasInFlightOutgoing = outgoing.some((entry) => entry.status !== MATCH_CHAT_OUTGOING_STATUSES.REJECTED);
  const sendBlock = isModerated ? getMatchChatSendBlock(matchChat, Date.now()) : null;
  const cooldownWaitMs = sendBlock?.kind === MATCH_CHAT_SEND_BLOCK_KINDS.COOLDOWN ? sendBlock.waitMs : null;

  useEffect(() => {
    onExpandedChange?.(isExpanded);
  }, [isExpanded, onExpandedChange]);

  // Развернули другую панель угла — чат сворачивается
  useCollapseOnCornerPanelRequest(isCollapseRequested, isExpanded, () => setIsExpanded(false));

  // Развернутый список всегда показывает последнее сообщение
  useEffect(() => {
    if (isExpanded && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [isExpanded, messages.length, outgoing.length]);

  useEffect(() => {
    if (isExpanded) {
      setReadCount(messages.length);
    }
  }, [isExpanded, messages.length]);

  // Кулдаун модерируемого чата кончится сам — перерисовываемся к этому
  // моменту, чтобы кнопка отправки ожила без действий игрока
  useEffect(() => {
    if (!cooldownWaitMs) {
      return undefined;
    }
    const timeoutId = setTimeout(() => setCooldownTick((tick) => tick + 1), cooldownWaitMs + 50);
    return () => clearTimeout(timeoutId);
  }, [cooldownWaitMs]);

  // Пока свои сообщения в пути или на проверке — проверяем, не потерялся ли
  // ответ сервера (обрыв связи): «зависшее» сообщение занимало бы место в
  // лимите сообщений на проверке, и чат встал бы до конца матча
  useEffect(() => {
    if (!hasInFlightOutgoing) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      dispatch(expireStaleMatchChatMessages(Date.now()));
    }, STALE_OUTGOING_CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [hasInFlightOutgoing, dispatch]);

  // Нажатие мимо формы (по полю, панелям, модалкам) уводит фокус из поля
  // сообщения. Само оно не уйдет: Phaser гасит mousedown/touchstart по канве
  // (preventDefault), и браузер фокус не переносит — WASD, стрелки и Enter
  // продолжали бы уходить в чат вместо игры. pointerdown приходит раньше
  // mousedown и Phaser его не гасит; слушаем в фазе перехвата
  useEffect(() => {
    if (!isExpanded || !canWrite) {
      return undefined;
    }
    const handlePointerDown = (event) => {
      const input = inputRef.current;
      if (input && document.activeElement === input && !formRef.current?.contains(event.target)) {
        input.blur();
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isExpanded, canWrite]);

  if (gameType !== GAME_TYPES.ONLINE && gameType !== GAME_TYPES.REPLAY) {
    return null;
  }

  // В модерируемом чате длинный текст не обрезается молча: его отклонили бы
  // целиком (см. normalizeModeratedChatText), а поле ввода и так не дает
  // набрать больше MODERATED_CHAT_MAX_LENGTH
  const textToSend = isModerated ? normalizeModeratedChatText(draft) : normalizeChatMessageText(draft);
  const isSendDisabled = !textToSend || (isModerated && !sendBlock.canSend);

  // Модерируемый чат: сообщение уходит в очередь модератора, а в общую
  // переписку попадет только одобренным — со стороны сервера
  const sendModeratedMessage = (socket) => {
    if (!sendBlock.canSend) {
      return;
    }
    const clientMessageId = createMatchChatClientMessageId();
    const now = Date.now();
    const entry = {clientMessageId, fromPlayerId: me.id, fromPlayerName: me.name, text: textToSend};
    // То, что сервер отсек бы до модератора (ссылка, секрет, служебные
    // символы), не отправляем вовсе — отказ виден сразу, а черновик остается
    // для правки
    const prefilter = prefilterChatMessage(textToSend);
    if (!prefilter.ok) {
      dispatch(matchChatMessageBlocked({...entry, reason: prefilter.reason, blockedAt: now}));
      return;
    }
    dispatch(matchChatSendStarted({...entry, sentAt: now}));
    socket.send(JSON.stringify({type: 'CHAT_SEND', payload: {clientMessageId, text: textToSend}}));
    setDraft('');
  };

  const sendMessage = () => {
    if (!me || !textToSend) {
      return;
    }
    // Без открытого сокета сообщение никуда не уйдет (матч завершен и связь
    // закрыта, идет переподключение), а локально оно показалось бы
    // отправленным. Не отправляем и не стираем черновик
    const socket = getNetworkSocket();
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      dispatch(showToast('Нет связи с матчем — сообщение не отправлено', 'error', 3000, true));
      return;
    }
    if (isModerated) {
      sendModeratedMessage(socket);
      return;
    }
    dispatch(addChatMessage(me.id, me.name, textToSend, Date.now()));
    setDraft('');
  };

  const handleDraftKeyDown = (event) => {
    stopKeyPropagation(event);
    if (event.key === 'Escape') {
      event.currentTarget.blur();
      return;
    }
    // Enter отправляет, Shift+Enter — перенос строки. Пока идет набор через
    // IME (составной символ), Enter подтверждает символ, а не отправляет
    const isImeComposing = event.nativeEvent?.isComposing || event.keyCode === IME_PROCESS_KEY_CODE;
    if (event.key === 'Enter' && !event.shiftKey && !isImeComposing) {
      event.preventDefault();
      sendMessage();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage();
  };

  // Свое сообщение модерируемого чата приходит в переписку только после
  // проверки — часто уже при свернутой панели. Непрочитанным оно не считается:
  // игрок его сам написал
  const unreadCount = isModerated
    ? messages.slice(readCount).filter((message) => message.fromPlayerId !== userId).length
    : Math.max(0, messages.length - readCount);
  const playerById = new Map((players || []).map((player) => [player.id, player]));
  // Имя автора — светлыми чернилами листа, цвет игрока — подчеркиванием (как
  // в панели реакций): темные цвета на базальте не читались бы
  const getAuthorStyle = (playerId) => ({
    '--chat-author-color': getPlayerHexColor(playerById.get(playerId)?.color),
  });
  const panelTitle = isModerated ? 'Чат матча' : 'Переписка игроков';

  const renderOutgoingEntry = (entry) => {
    const isRejected = entry.status === MATCH_CHAT_OUTGOING_STATUSES.REJECTED;
    return (
      <div
        key={entry.clientMessageId}
        className={`chat-panel_message chat-panel_outgoing ${
          addClassNameIfTrue(isRejected, 'chat-panel_outgoing-rejected')
        }`}
      >
        <span className={'chat-panel_author'} style={getAuthorStyle(entry.fromPlayerId)}>
          {entry.fromPlayerName} (вы):
        </span>
        <span className={'chat-panel_text'}>{entry.text}</span>
        <span className={'chat-panel_outgoing-status'}>
          {isRejected ? getChatRejectionText(entry.reason, entry) : OUTGOING_STATUS_TEXTS[entry.status]}
        </span>
        {isRejected && (
          <button
            type={'button'}
            className={'chat-panel_dismiss-button'}
            onClick={() => dispatch(dismissMatchChatMessage(entry.clientMessageId))}
            title={'Скрыть'}
            aria-label={'Скрыть'}
          >
            ✕
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`chat-panel ${addClassNameIfTrue(!isExpanded, 'chat-panel-collapsed')}`}>
      {!isExpanded ? (
        <>
          {/* Нейтральная плитка дизайн-системы матча. Счетчик — соседом, а не
              внутри: срезанные углы кнопки (clip-path) обрезали бы его */}
          <GameButton
            size={GAME_BUTTON_SIZES.ICON}
            onClick={() => setIsExpanded(true)}
            className={'chat-panel_toggle'}
            title={isModerated ? 'Чат матча' : 'Переписка игроков (только локальная разработка)'}
          >
            <EngravedIcon src={'/img/icons/chat.png'} size={24} alt={'chat'}/>
          </GameButton>
          {unreadCount > 0 && (
            <span className={'chat-panel_badge'}>{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </>
      ) : (
        // Развернутая панель — лист базальта, как панель реакций
        <GamePanel shadow={true} className={'chat-panel-expanded'} data-toasts-avoid={true}>
          <div className={'chat-panel_body'}>
            <div className={'chat-panel_side'}>
              <EngravedIcon className={'chat-panel_side-icon'} src={'/img/icons/chat.png'} size={25} alt={'chat'}/>
              <button
                onClick={() => setIsExpanded(false)}
                className={'footer-heading-btn chat-panel_collapse-button'}
                title={'Свернуть'}
              >
                <EngravedIcon src={'/img/icons/expand.png'} size={25} alt={'collapse'}/>
              </button>
            </div>

            <div className={'chat-panel_content'}>
              <div className={'chat-panel_title'}>{panelTitle}</div>
              {isChatPaused && (
                <div className={'chat-panel_paused'}>{MODERATED_CHAT_PAUSED_TEXT}</div>
              )}
              <div className={'chat-panel_messages'} ref={listRef}>
                {messages.length === 0 && outgoing.length === 0 ? (
                  <div className={'chat-panel_empty'}>Сообщений пока нет</div>
                ) : (
                  messages.map((message, index) => (
                    <div key={index} className={'chat-panel_message'}>
                      <span
                        className={'chat-panel_author'}
                        style={getAuthorStyle(message.fromPlayerId)}
                      >
                        {message.fromPlayerName}
                        {isModerated && playerById.get(message.fromPlayerId)?.isAi && (
                          <AiPlayerMark className={'chat-panel_ai-mark'}/>
                        )}
                        {message.fromPlayerId === userId ? ' (вы)' : ''}:
                      </span>
                      <span className={'chat-panel_text'}>{message.text}</span>
                    </div>
                  ))
                )}
                {outgoing.map(renderOutgoingEntry)}
              </div>

              {canWrite && (
                <form className={'chat-panel_form'} onSubmit={handleSubmit} ref={formRef}>
                  <textarea
                    ref={inputRef}
                    className={'chat-panel_input'}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={handleDraftKeyDown}
                    onKeyPress={stopKeyPropagation}
                    maxLength={isModerated ? MODERATED_CHAT_MAX_LENGTH : CHAT_MESSAGE_MAX_LENGTH}
                    rows={2}
                    disabled={isChatPaused}
                    placeholder={'Сообщение всем игрокам'}
                    title={'Enter — отправить, Shift+Enter — новая строка, Esc — вернуться к игре'}
                    aria-label={'Сообщение в чат матча'}
                  />
                  <GameButton
                    type={'submit'}
                    className={'chat-panel_send-button'}
                    disabled={isSendDisabled}
                    title={'Отправить'}
                  >
                    Отправить
                  </GameButton>
                </form>
              )}
              {canWrite && isModerated && (
                <div className={'chat-panel_hint'}>
                  {sendBlock.kind === MATCH_CHAT_SEND_BLOCK_KINDS.PENDING_LIMIT
                    ? MODERATED_CHAT_PENDING_LIMIT_HINT
                    : MODERATED_CHAT_HINT}
                </div>
              )}
            </div>
          </div>
        </GamePanel>
      )}
    </div>
  );
};

export default ChatPanel;
